import React, { memo, useEffect, useRef, useState } from 'react'
import { useSelector } from 'react-redux'
import { RootState } from '@/app/store'
import { disableBodyScroll, enableBodyScroll } from 'body-scroll-lock'
import { Pagination, Virtual } from 'swiper/modules'
import { Swiper, SwiperSlide } from 'swiper/react'
import { useForm } from 'react-hook-form'
import Button from '../Button'
import { useTranslation } from 'react-i18next'
import Checkbox from '../Form/Checkbox'

type DialogPopupType = {
  hookpoint:
    | 'LOGIN'
    | 'REGISTER'
    | 'HOME'
    | 'SPOTS'
    | 'PRESENTS'
    | 'AR_PHOTO_FRAME_SPOTDETAIL'
    | 'AR_PHOTO_FRAME_REWARDSDETAIL'
  onClickFunc?: () => void
  dialogState?: boolean
  dialogSetState?: React.Dispatch<React.SetStateAction<boolean>>
}

const DialogPopup = memo((props: DialogPopupType) => {
  const { hookpoint, onClickFunc, dialogState: externalDialogState, dialogSetState: externalDialogSetState } = props

  if (externalDialogState === false) return <div>{''}</div>

  const { t } = useTranslation()
  const swiperRef = useRef(null)
  const dialogElem = useRef(null)
  const [internalDialogState, internalDialogSetState] = useState(true)
  const [isSwiperEnd, setIsSwiperEnd] = useState(false)
  const [swiperKey, setSwiperKey] = useState(0)

  const { register } = useForm()

  const dialogState = externalDialogState !== undefined ? externalDialogState : internalDialogState
  const dialogSetState = externalDialogSetState !== undefined ? externalDialogSetState : internalDialogSetState
  const dataCampaign = useSelector((state: RootState) => state.apiAsync.campaign)

  const getCurrentPopup = (popup: Api.CampaignResBody['popup']) => {
    if (!popup) return null

    const getPopupStatus = (key: string) => localStorage.getItem(`${process.env.NEXT_PUBLIC_SERVICE_NAME}-${key}-popup`)
    const popupStatus = getPopupStatus(hookpoint)

    if (popupStatus === 'hide') return null

    // 特殊なケースの処理(スポットのARフォトと特典のARフォトのポップアップ「次回以降表示しない」機能を同期させないため)
    if (hookpoint === 'AR_PHOTO_FRAME_SPOTDETAIL' || hookpoint === 'AR_PHOTO_FRAME_REWARDSDETAIL') {
      return popup.find((item) => item.hookpoint === 'AR_PHOTO_FRAME')
    }

    return popup.find((item) => item.hookpoint === hookpoint)
  }

  const currentPopup = getCurrentPopup(dataCampaign?.popup)
  console.log(currentPopup)

  useEffect(() => {
    if (!dataCampaign) return
    if (!currentPopup && dialogState && typeof onClickFunc === 'function') {
      // 次回以降表示しないかつ関数だけ実行するパターン
      onClickFunc()
      return
    }
  }, [dataCampaign, currentPopup])

  useEffect(() => {
    if (swiperRef.current) {
      setSwiperKey((prevKey) => prevKey + 1)

      if (currentPopup?.images.length === 1) {
        setIsSwiperEnd(true)
      }
    }
  }, [currentPopup])

  useEffect(() => {
    return () => {
      if (swiperRef.current && swiperRef.current.swiper) {
        swiperRef.current.swiper.destroy(true, true)
        swiperRef.current.swiper = null
      }
    }
  }, [])

  useEffect(() => {
    if (dialogState) {
      disableBodyScroll(dialogElem.current)
    } else {
      enableBodyScroll(dialogElem.current)
    }
  }, [dialogState])

  const CloseButton = () => {
    enableBodyScroll(dialogElem.current)
    dialogSetState(false)

    const checkbox = document.getElementById(`hidePopup-${hookpoint}`) as HTMLInputElement
    if (currentPopup && checkbox.checked) {
      localStorage.setItem(`${process.env.NEXT_PUBLIC_SERVICE_NAME}-${hookpoint}-popup`, 'hide')
    }
  }

  const goNext = () => {
    if (swiperRef.current && swiperRef.current.swiper) {
      swiperRef.current.swiper.slideNext()
    }
  }

  const onClickButton = () => {
    if (isSwiperEnd) {
      CloseButton()
      if (typeof onClickFunc === 'function') {
        onClickFunc()
      }
    } else {
      goNext()
    }
  }

  if (!currentPopup) return <div>{''}</div>
  return (
    <div className="c-dialog c-dialog--popup" id="my-dialog" aria-hidden={!dialogState}>
      <div className="c-dialog-overlay" onClick={() => CloseButton()}></div>
      <div className="c-dialog-wrap">
        <div role="dialog" className="c-dialog-content--popup" aria-labelledby="dialog" ref={dialogElem}>
          <Swiper
            modules={[Pagination, Virtual]}
            speed={1000}
            loop={false}
            autoHeight={true}
            spaceBetween={30}
            slidesPerView={1}
            centeredSlides={true}
            allowTouchMove={false}
            ref={swiperRef}
            onSlideChange={(swiper) => setIsSwiperEnd(swiper.isEnd)}
            pagination={{
              el: `#pagination-${hookpoint}`,
              type: 'bullets',
              clickable: true,
            }}
            virtual
          >
            {currentPopup?.images.map((image, index) => (
              <SwiperSlide key={index}>
                <img src={image} alt="" />
              </SwiperSlide>
            ))}
          </Swiper>
          <div
            className={`mb-[50px] flex items-center justify-center ${
              isSwiperEnd ? '' : 'pointer-events-none opacity-0'
            }`}
          >
            <Checkbox
              htmlfor={`hidePopup-${hookpoint}`}
              register={register}
              formValueSplit={[t('common.popup.hide')]}
            />
          </div>
          <div className="mt-auto">
            <div id={`pagination-${hookpoint}`} className="flex justify-center space-x-1.75 mb-8" />
            <Button text={isSwiperEnd ? t('common.popup.finish') : t('common.popup.next')} onClick={onClickButton} />
          </div>
        </div>
      </div>
    </div>
  )
})

export default DialogPopup
